// Source of truth for the "Privacy Policy" text shown in the signup consent
// dialog. Converted from the Word original supplied by Perx Group; edit this
// file (not the .docx) when the document changes, and bump `version` so the
// consent record can be traced back to the wording the member actually saw.

export const PRIVACY_POLICY = {
    title: "Perx Group Privacy Policy",
    version: "2026-09-07",
    body: `# Perx Group Limited — Privacy Policy

**Effective Date:** 7 September 2026 · Operating in Hong Kong

Your privacy is important to us, and so is being transparent about how we collect, use, store, and share information about you. This Privacy Policy explains how Perx Group Limited ("Perx", "we", "our", "us") handles personal information when you use our websites, membership platform, products, services, or otherwise interact with us. It also describes the choices and rights you have under Hong Kong's Personal Data (Privacy) Ordinance (PDPO).

## 1. What Information We Collect

We collect information in three main ways:

### a. Information you provide directly

This includes:

- Account registration details (name, email, phone number, password)

- Profile information and preferences

- Information submitted when requesting quotes, booking appointments, or making purchases

- Documents or data you upload (e.g., insurance forms, business onboarding details)

- Payment information for membership fees or Points Pal transactions

- Communications with us, including support requests and feedback

### b. Information collected automatically

When you use our website or digital tools, we may collect:

- Device and browser details

- IP address and location‑based metadata

- Usage analytics, page interactions, and clickstream data

- Cookies and similar tracking technologies

### c. Information from vendors or partners

When you proceed with a purchase or service through Perx, vendors may provide us with:

- Application or quotation status

- Service fulfilment updates

- Transaction confirmations

- Compliance‑related information required for regulated products (e.g., insurance)

## 2. How We Use Information We Collect

We use your information to:

- Provide, operate, and maintain the Perx membership platform

- Facilitate quotes, bookings, and purchases you request

- Authenticate your account and enable secure access

- Process membership payments and Points Pal transactions

- Deliver customer support and respond to enquiries

- Improve our products, services, and user experience

- Personalize features, recommendations, and content

- Conduct research, analytics, and service development

- Detect and prevent fraud, abuse, or security incidents

- Comply with Hong Kong legal, regulatory, and audit requirements

Where required by law, we rely on appropriate legal grounds such as consent, contractual necessity, legitimate interests, or compliance with legal obligations.

## 3. How We Share Information We Collect

Perx does not sell, rent, or distribute member information to advertisers or unrelated third parties.

We share information only in the following circumstances:

### a. When you agree to proceed with a purchase or service

If you choose to purchase a product or service through Perx, we share the necessary details with the relevant vendor so they can:

- Provide quotations

- Process your application

- Deliver the service or product

- Contact you for fulfilment or compliance purposes

This sharing occurs only with your explicit agreement.

### b. Service providers

We work with trusted third‑party providers who help us operate our platform, including:

- Hosting and cloud infrastructure

- Payment processors

- Customer support tools

- Analytics and security services

These providers are contractually required to protect your data.

### c. Administrators of managed business accounts

For business software products (e.g., QC4, PM Suite), organizational administrators may access and manage user information within their company's account.

### d. Legal, regulatory, and safety requirements

We may disclose information to:

- Comply with Hong Kong laws or regulatory requests

- Enforce our Terms of Service

- Respond to emergencies

- Protect our legal rights or those of our users

### e. Business transfers

If Perx undergoes a merger, acquisition, or sale of assets, personal information may be transferred as part of the transaction.

## 4. How We Store and Protect Information

We store and protect information using industry‑standard technical and organizational measures, including encryption, access controls, secure hosting environments, and monitoring systems. As with any system, no method of storage or transmission is completely secure, but we take reasonable steps to safeguard your data.

Retention periods vary by data type:

- Account information is retained while your membership is active and for a reasonable period afterward

- Transaction and vendor‑related information is retained for compliance and audit purposes

- Backup data may persist temporarily before secure deletion

Where immediate deletion is not possible (e.g., backups), data will be isolated until removal is feasible.

## 5. Your Choices and Rights

Under Hong Kong's PDPO, you have the right to:

- Access the personal data we hold about you

- Request correction of inaccurate information

- Request information about our data policies and practices

- Withdraw consent for marketing communications

- Request deletion of data where appropriate

Some controls are available directly in your account settings. Requests can be made by contacting us at: enquiry@perx.group

## 6. International Transfers

Perx operates in Hong Kong. If personal information is processed or stored outside Hong Kong (e.g., cloud hosting), we ensure appropriate safeguards are in place to protect your data.

## 7. Special Notices for Business Users

If your access to Perx business products (QC4, PM Suite) is provided through your employer:

- Your organization's administrator may access and control your account

- Organizational policies may differ from this Privacy Policy

- Requests regarding access or deletion may need to be directed to your administrator

## 8. Children's Privacy

Perx services are not directed to individuals under 18. If we learn that we have collected personal information from a minor, we will delete it promptly.

## 9. Updates to This Policy

We may update this Privacy Policy from time to time. Significant changes will be communicated through our website or member notifications. Your continued use of Perx after updates indicates acceptance of the revised policy.

## 10. Contact Us

For questions, access requests, or data concerns, contact:

Perx Group Limited  
enquiry@perx.group
`,
};
